import _ from 'lodash';
import { action, makeObservable } from 'mobx';
import { AssignCarrierServiceAndWarehouseForm } from 'modules/order/forms';
import { BaseViewStore } from 'core/stores';
import {
	notificationError,
	isSuperAdminOrganization,
	getDistinctProductIds,
	mapCarrierServiceWithRates,
	translate,
	shouldUseBuyShipping,
	getBulkItemsSellerId,
} from 'core/utils';
import { ProductInventoryStore } from 'modules/product-inventory/stores';
import { FulfillmentOrderProcessingStore, FulfillmentOrderStore } from 'modules/order/stores';
import { CarrierServiceStore } from 'modules/carrier/stores';
import { RateServiceStore } from 'modules/rate/stores';
import { ApplicationStore } from 'platform/application/stores';
import { channelType, onAssignStatuses, sourceOfChange } from 'common/constants';
import { BaasicDropdownViewStore } from 'common/stores';

class AssignCarrierServiceAndWarehouseViewStore extends BaseViewStore {
	warehouseDropdownStore = null;
	carrierServiceDropdownStore = null;
	selectedItem = null;
	selectedItems = null;
	isBulkAction = false;
	isAllAmazon = false;

	form = new AssignCarrierServiceAndWarehouseForm({
		onSuccess: async () => {
			if (this.isBulkAction) {
				await this.assignCarrierServiceAndWarehouseBulk();
			} else {
				await this.assignCarrierServiceAndWarehouse();
			}
			this.modalParams.success();
		},
	});

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.fulfillmentOrderProcessingStore = new FulfillmentOrderProcessingStore(rootStore);
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.initialize(_.get(this.modalParams, 'data.selectedItemId'), _.get(this.modalParams, 'data.selectedItemIds'));
	}

	@action.bound
	initialize = async (selectedItemId, selectedItemIds) => {
		this.suspend();
		if (selectedItemIds != null) {
			this.isBulkAction = true;
			await this.findFulfillmentOrders(selectedItemIds);
			this.isAllAmazon =
				_.find(this.selectedItems, item => item.order.channel.channelType.name !== channelType.amazon) === undefined
					? true
					: false;
			this.form.updateForm({
				useBuyShipping: false,
				ignoreDeliverByDate: this.isAllAmazon ? !this.useBuyShipping : true,
			});
		} else {
			await this.findFulfillmentOrders([selectedItemId]);
			this.isVendorOrder = this.selectedItem.order.channel.channelType.name === channelType.amazonVendor;
			this.isLtlOrder =
				_.get(this.selectedItem, 'order.channel.name') === 'LTL Freight' ||
				_.get(this.selectedItem, 'order.requiredCarrierService') == 'LTL';
			this.ignoreDeliverByDate =
				_.get(this.selectedItem, 'order.channel.channelType.name') === channelType.amazon
					? !shouldUseBuyShipping(this.selectedItem, this.form)
					: true;
			this.form.$('ignoreDeliverByDate').set('value', this.ignoreDeliverByDate);
		}
		this.initializeDropdowns();
		this.resume();
	};

	@action.bound
	initializeDropdowns = () => {
		const carrierServiceDropdownStoreProps = {
			field: this.form.$('assignedCarrierServiceId'),
		};
		const warehouseDropdownStoreProps = {
			field: this.form.$('assignedWarehouseId'),
		};
		if (this.isBulkAction || this.isVendorOrder || this.isLtlOrder) {
			Object.assign(carrierServiceDropdownStoreProps, {
				store: new CarrierServiceStore(this.rootStore),
				filterProperties: {
					embed: ['carrier'],
					requiredCarrierService: this.isVendorOrder
						? this.selectedItem.order.requiredCarrierService
						: this.isLtlOrder
						? 'LTL'
						: null,
				},
			});
			Object.assign(warehouseDropdownStoreProps, {
				store: new ApplicationStore(this.rootStore),
				itemTemplate: { value: 'id', label: 'applicationTitle' },
				filterProperties: {
					warehouseOnly: true,
					isOrganizationActive: true,
					sellerWarehousesOnlyOrganizationId: getBulkItemsSellerId(this.selectedItems),
				},
			});
		} else {
			Object.assign(carrierServiceDropdownStoreProps, {
				store: new RateServiceStore(this.rootStore),
				fetchAll: true,
				fetchFunc: 'getRates',
				fetchInitialValues: false,
				filterCallback: mapCarrierServiceWithRates,
			});
			Object.assign(warehouseDropdownStoreProps, {
				store: new ProductInventoryStore(this.rootStore),
				itemTemplate: { value: 'assignedWarehouseId', label: 'assignedWarehouse.applicationTitle' },
				filterProperties: {
					fulfillmentOrderId: this.selectedItem.id,
					calculateInventory: true,
					filterInapplicableWarehouses: true,
					includeOnlySellerWarehouses: true,
					sellerWarehousesOnlyOrganizationId: this.selectedItem.sellerId,
					productIds: getDistinctProductIds(this.selectedItem.fulfillmentOrderItems),
					isOrganizationActive: true,
					useSafeQuantity: false,
					embed: ['assignedWarehouse', 'product', 'product.multiPackageProductParts'],
				},
				onChangeCallback: async selectedOption => {
					this.suspend();
					this.carrierServiceDropdownStore.setValue(null);
					await this.carrierServiceDropdownStore.updateFilter({
						fulfillmentOrderId: this.selectedItem.id,
						warehouseId: selectedOption.value,
						ignoreDeliverByDate: this.form.$('ignoreDeliverByDate').value,
						useBuyShipping: this.form.$('useBuyShipping').value,
					});
					this.resume();
				},
			});
		}
		this.carrierServiceDropdownStore = new BaasicDropdownViewStore({ ...carrierServiceDropdownStoreProps });
		this.warehouseDropdownStore = new BaasicDropdownViewStore({ ...warehouseDropdownStoreProps });
	};

	@action.bound
	findFulfillmentOrders = async selectedItemIds => {
		const result = (await this.fulfillmentOrderStore.findFulfillmentOrder({
			page: 1,
			rpp: 100,
			ids: selectedItemIds,
			embed: [
				'fulfillmentOrderStatus',
				'fulfillmentOrderItems',
				'fulfillmentOrderItems.product',
				'order',
				'order.channel',
				'order.channel.channelType',
			],
		})).item;
		if (this.isBulkAction) {
			this.selectedItems = result;
		} else {
			this.selectedItem = result.pop();
		}
	};

	@action.bound
	assignCarrierServiceAndWarehouse = async () => {
		const data = {
			carrierId: _.get(this.carrierServiceDropdownStore.value, 'extra.carrier.id'),
			rate: this.carrierServiceDropdownStore.value.extra.rate,
			surcharge: this.carrierServiceDropdownStore.value.extra.rateComposition
				? this.carrierServiceDropdownStore.value.extra.rateComposition.surcharge
				: 0,
			fulfillmentOrderId: this.selectedItem.id,
			...this.form.values(),
		};
		try {
			await this.fulfillmentOrderProcessingStore.assignCarrierServiceAndWarehouse(
				data,
				sourceOfChange.assignCarrierAndWarehouse
			);
			this.rootStore.notificationStore.success(['FULFILLMENTORDER.ASSIGNMODAL.SUCCESS']);
		} catch (err) {
			if (err.statusCode == 400 && _.get(err, 'data.message') == 'FULFILLMENTORDER.ALREADYPROCESSING') {
				this.rootStore.notificationStore.error([
					'FULFILLMENTORDER.ALREADYPROCESSING',
					{ id: this.selectedItem.fulfillmentOrderId },
				]);
			} else {
				this.rootStore.notificationStore.error([
					'FULFILLMENTORDER.ASSIGNMODAL.FAIL',
					{ id: this.selectedItem.fulfillmentOrderId },
				]);
			}
		}
	};

	@action.bound
	assignCarrierServiceAndWarehouseBulk = async () => {
		const notificationId = this.rootStore.notificationStore.load(translate('BULKNOTIFICATION.ASSIGN'));
		try {
			const { ignoreDeliverByDate, ...otherValues } = this.form.values();
			const selectedItemsIds = [];
			const notProcessableItems = [];

			_.each(this.selectedItems, selectedItem => {
				const index = _.findIndex(onAssignStatuses, item => item == selectedItem.fulfillmentOrderStatus.abrv);
				if (index != -1) {
					selectedItemsIds.push(selectedItem.id);
				} else {
					notProcessableItems.push(selectedItem);
				}
			});
			if (selectedItemsIds.length > 0) {
				this.modalParams.close();
				const count = await this.fulfillmentOrderProcessingStore.assignCarrierServiceAndWarehouseBulk(
					{
						fulfillmentOrderIds: selectedItemsIds,
						ignoreDeliverByDate: this.isSuperAdminOrganization ? ignoreDeliverByDate : true,
						...otherValues,
					},
					sourceOfChange.assignCarrierAndWarehouseBulk
				);
				if (count > 0) {
					if (count == selectedItemsIds.length) {
						this.rootStore.notificationStore.success([
							'FULFILLMENTORDER.ASSIGNMODAL.SUCCESSBULK',
							{
								count: count,
								countMax: selectedItemsIds.length,
								notProcessable: notProcessableItems.length,
							},
						]);
					} else {
						this.rootStore.notificationStore.success([
							'FULFILLMENTORDER.ASSIGNMODAL.PARTIALSUCCESSBULK',
							{
								count: count,
								countMax: selectedItemsIds.length,
								notProcessable: notProcessableItems.length,
							},
						]);
					}
				} else {
					this.rootStore.notificationStore.error('ORDERVIEW.NOVALIDORDERSELECTEDFORASSIGN');
				}
			} else {
				this.rootStore.notificationStore.error('ORDERVIEW.NOVALIDORDERSELECTEDFORASSIGN');
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.rootStore.notificationStore.dismiss(notificationId);
	};

	@action.bound
	onCheck = async () => {
		this.carrierServiceDropdownStore.clear();
		if (!this.form.$('assignedWarehouseId').isEmpty && !this.isBulkAction) {
			this.suspend();
			const { useBuyShipping } = this.form.values();
			await this.carrierServiceDropdownStore.updateFilter({
				useBuyShipping,
				ignoreDeliverByDate: !useBuyShipping,
			});
			this.form.$('ignoreDeliverByDate').value = !useBuyShipping;
			this.resume();
		}
	};

	@action.bound
	onIgnoreDeliverByDateChange = async e => {
		if (!this.isBulkAction) {
			if (this.selectedItem.order.channel.channelType.name === channelType.amazon) {
				this.form.$('useBuyShipping').value = !e.currentTarget.checked;
			}
			if (this.warehouseDropdownStore.value) {
				if (this.carrierServiceDropdownStore.value) this.carrierServiceDropdownStore.clear();
				await this.carrierServiceDropdownStore.updateFilter({
					ignoreDeliverByDate: e.currentTarget.checked,
				});
			}
		} else {
			this.form.$('useBuyShipping').set('value', !e.currentTarget.checked);
		}
	};
}

export default AssignCarrierServiceAndWarehouseViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/AssignCarrierServiceAndWarehouseViewStore.js